import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { NotificationIcon } from "./NotificationIcon";
import { useMarkRead, useDeleteNotification } from "../../../hooks/useNotifications";
import { useMarkSeen } from "./SeenBatcher";
import { Trash2 } from "lucide-react";
import { trackEvent } from "../../../utils/analytics";

/** How long a row must stay in view before it counts as read. */
const SEEN_DWELL_MS = 1000;
/** Most of the row must be showing — a sliver at the edge is not "seen". */
const SEEN_VISIBILITY = 0.6;

export const NotificationRow = ({ notification, onClickCallback }) => {
  const { id, type, title, body, readAt, createdAt, actor, data } = notification;
  const { mutate: markRead } = useMarkRead();
  const { mutate: deleteNotif } = useDeleteNotification();
  const markSeen = useMarkSeen();
  const navigate = useNavigate();
  const rowRef = useRef(null);

  const isUnread = !readAt;

  // Mark read once the row has actually been looked at: mostly visible, for a
  // full second, in a foreground tab. Scrolling straight past does not count,
  // and neither does sitting in a background tab — a notification list left
  // open behind other windows should not quietly clear itself.
  useEffect(() => {
    const el = rowRef.current;
    if (!isUnread || !el || typeof IntersectionObserver === "undefined") return;

    let dwellTimer = null;
    const cancel = () => {
      if (dwellTimer !== null) {
        clearTimeout(dwellTimer);
        dwellTimer = null;
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && document.visibilityState === "visible") {
          if (dwellTimer === null) {
            dwellTimer = setTimeout(() => {
              dwellTimer = null;
              markSeen(id);
            }, SEEN_DWELL_MS);
          }
        } else {
          cancel();
        }
      },
      { threshold: SEEN_VISIBILITY }
    );

    observer.observe(el);
    document.addEventListener("visibilitychange", cancel);
    return () => {
      cancel();
      observer.disconnect();
      document.removeEventListener("visibilitychange", cancel);
    };
  }, [id, isUnread, markSeen]);
  
  // Format relative time (e.g., "5 min ago", "2 hours ago")
  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return "Just now";
    
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    const minutes = Math.floor(diffInSeconds / 60);
    if (minutes < 60) return rtf.format(-minutes, 'minute');
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return rtf.format(-hours, 'hour');
    
    const days = Math.floor(hours / 24);
    if (days < 30) return rtf.format(-days, 'day');
    
    const months = Math.floor(days / 30);
    if (months < 12) return rtf.format(-months, 'month');
    
    const years = Math.floor(months / 12);
    return rtf.format(-years, 'year');
  };

  const getTargetUrl = () => {
    switch (type) {
      case "POST_LIKED":
      case "POST_SAVED":
      case "POST_COPIED":
      case "COMMENT_RECEIVED":
      case "POST_APPROVED":
        return data?.postSlug ? `/community/${data.postSlug}` : "#";
      case "POST_REJECTED":
        // /community/:slug/edit was never a registered route — this fell
        // through to the catch-all NotFound page. The real place to fix and
        // resubmit a rejected post is the "Fix and Resubmit" flow on the
        // dashboard (ResubmitFlow.jsx), which Dashboard.jsx now opens
        // automatically when it sees this query param.
        return data?.postSlug ? `/dashboard?resubmit=${data.postSlug}` : "/dashboard";
      case "NEW_FOLLOWER":
        return data?.actorHandle ? `/u/${data.actorHandle}` : "#";
      case "BADGE_AWARDED":
        // Your own badges live on /account now. This used to build /u/<handle>,
        // which both bounces through a redirect and dead-ends at "#" for a user
        // who never set a handle — on the one notification whose whole purpose
        // is to show them the badge they just earned.
        return "/account";
      case "CREDIT_EARNED":
        return "/rewards";
      default:
        return "#";
    }
  };

  const handleClick = (e) => {
    e.preventDefault();
    trackEvent("notification_clicked", { notification_id: id, type, was_unread: isUnread });
    if (isUnread) markRead(id);

    const targetUrl = getTargetUrl();
    if (targetUrl !== "#") {
      navigate(targetUrl);
    }

    if (onClickCallback) onClickCallback();
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm("Delete this notification?")) {
      trackEvent("notification_deleted", { notification_id: id, type });
      deleteNotif(id);
    }
  };

  return (
    <div
      ref={rowRef}
      onClick={handleClick}
      className={`group relative flex items-start gap-4 p-4 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 ${isUnread ? "bg-indigo-50/30" : "bg-white"}`}
    >
      {/* Unread indicator */}
      <div className="absolute left-0 top-0 bottom-0 w-1">
        {isUnread && <div className="h-full w-full bg-indigo-500 rounded-r" />}
      </div>

      {/* Avatar or System Icon */}
      <div className="relative shrink-0">
        {actor ? (
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold uppercase overflow-hidden border border-indigo-200">
            {actor.name ? actor.name.charAt(0) : actor.handle.charAt(0)}
          </div>
        ) : (
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200">
            <NotificationIcon type={type} className="w-5 h-5 text-gray-600" />
          </div>
        )}
        
        {/* Sub-icon to show what action happened when there's an actor */}
        {actor && (
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow-sm border border-gray-100">
            <NotificationIcon type={type} className="w-3 h-3" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-semibold text-gray-900 leading-snug break-words">
          {title}
        </p>
        
        {body && (
          <p className="text-[13px] text-gray-500 mt-1 line-clamp-2">
            {body}
          </p>
        )}

        {/* Rejection special handling */}
        {type === "POST_REJECTED" && data?.rejectionReason && (
          <div className="mt-2 p-3 bg-red-50 rounded-md border border-red-100">
            <p className="text-sm font-semibold text-red-800">Reason: {data.rejectionReason}</p>
            {data.rejectionNote && <p className="text-xs text-red-700 mt-1">{data.rejectionNote}</p>}
            <button className="mt-2 text-xs font-medium bg-white text-red-700 px-3 py-1.5 rounded border border-red-200 shadow-sm hover:bg-red-50">
              Edit & Resubmit
            </button>
          </div>
        )}

        <p className="text-[12px] text-gray-400 mt-1.5 font-medium">
          {getRelativeTime(createdAt)}
        </p>
      </div>

      {/* Delete action */}
      <button 
        onClick={handleDelete}
        className="opacity-0 group-hover:opacity-100 shrink-0 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all focus:opacity-100"
        title="Delete notification"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};
